import { useRef, useState } from "react";
import { toast } from "sonner";
import { Play, Square, Loader2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { playMemory } from "../../lib/pensieve";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";

const API = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ["Identity", "Family", "Place", "Childhood", "Science", "Habits"];

const Field = ({ label, hint, children }) => (
  <div>
    <span className="lab-label">{label} {hint && <span className="normal-case tracking-normal text-slate-600">- {hint}</span>}</span>
    <div className="mt-1">{children}</div>
  </div>
);

// Shared memory editor for the Bench review and the manual form. onChange receives a patch.
export const MemoryFields = ({ memory = {}, sfxPrompt = "", onChange, idPrefix = "memory" }) => {
  const [rendering, setRendering] = useState(false);
  const [playing, setPlaying] = useState(false);
  const ctrl = useRef(null);
  const setM = (k) => (e) => onChange({ memory: { ...memory, [k]: e.target.value } });

  const stop = () => { ctrl.current?.stop(); ctrl.current = null; setPlaying(false); setRendering(false); };
  const preview = async () => {
    if (ctrl.current) { stop(); return; }
    if (!memory.script?.trim()) { toast.error("Write the memory first"); return; }
    setRendering(true);
    try {
      await api.post("/admin/preview-memory", { script: memory.script, sfx_prompt: sfxPrompt });
      const c = await playMemory({
        voiceUrl: `${API}/api/admin/preview-memory/voice?script=${encodeURIComponent(memory.script)}`,
        sfxUrl: sfxPrompt ? `${API}/api/admin/preview-memory/sfx?prompt=${encodeURIComponent(sfxPrompt)}` : null,
        onEnd: () => { ctrl.current = null; setPlaying(false); },
      });
      ctrl.current = c;
      setPlaying(true);
    } catch (err) {
      toast.error(errMsg(err, "Could not render the memory audio"));
    } finally {
      setRendering(false);
    }
  };

  return (
    <div data-testid="memory-fields" className="grid gap-3 border border-amber-300/30 bg-amber-300/5 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="scientist-label">The memory inside</p>
        <button type="button" data-testid={`${idPrefix}-preview`} data-state={playing ? "playing" : rendering ? "rendering" : "idle"} onClick={preview} className="lab-btn-ghost py-1.5 text-sm">
          {playing ? <Square size={14} /> : rendering ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
          {playing ? "Stop" : rendering ? "Rendering voice & sound (10-20s)..." : "Preview memory"}
        </button>
      </div>
      <Field label="Title"><input data-testid={`${idPrefix}-title`} value={memory.title || ""} onChange={setM("title")} className="lab-input w-full" /></Field>
      <div className="grid gap-3 grid-cols-[90px_90px_1fr]">
        <Field label="Age"><input data-testid={`${idPrefix}-age`} type="number" min="0" max="99" value={memory.age ?? ""} onChange={(e) => onChange({ memory: { ...memory, age: Number(e.target.value) } })} className="lab-input w-full font-mono" /></Field>
        <Field label="Year"><input data-testid={`${idPrefix}-year`} type="number" min="1900" max="2100" value={memory.year ?? ""} onChange={(e) => onChange({ memory: { ...memory, year: Number(e.target.value) } })} className="lab-input w-full font-mono" /></Field>
        <Field label="Category">
          <Select value={memory.category || "Childhood"} onValueChange={(category) => onChange({ memory: { ...memory, category } })}>
            <SelectTrigger data-testid={`${idPrefix}-category`} className="w-full rounded-none border-slate-700 bg-slate-900 font-mono text-xs uppercase tracking-widest"><SelectValue /></SelectTrigger>
            <SelectContent className="lab-dialog">
              {CATEGORIES.map((c) => <SelectItem key={c} value={c} className="font-mono text-xs uppercase tracking-widest">{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      </div>
      <Field label="His voice" hint="first person, a teenager remembering - direct it with tags like [soft laugh], [quietly], [pause] (heard, never shown)">
        <textarea data-testid={`${idPrefix}-script`} rows={4} value={memory.script || ""} onChange={setM("script")} className="lab-input w-full text-sm leading-relaxed" />
      </Field>
      <Field label="Clue for the case file" hint="one third-person fact">
        <input data-testid={`${idPrefix}-clue`} value={memory.clue || ""} onChange={setM("clue")} className="lab-input w-full text-sm" />
      </Field>
      <Field label="Sound bed" hint="describe the ambience, no music or speech">
        <input data-testid={`${idPrefix}-sfx`} value={sfxPrompt} onChange={(e) => onChange({ sfx_prompt: e.target.value })} className="lab-input w-full text-sm" />
      </Field>
    </div>
  );
};
